// src/library/chat/upload-next-submitted.tsx
'use client';

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import {
  Button,
  View,
  Flex,
  useAuthenticator,
  Badge,
  Text,
} from "@aws-amplify/ui-react";
import { Container } from "../container";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { Auth } from "aws-amplify";

import {
  GraphqlQuery,
  getIdTokenWithFallback,
  extractUserIdFromToken,
} from "@heartcheck/shared";

type FileStatus = "idle" | "uploading" | "done" | "error";

type UploadItem = {
  id: string;
  file: File;
  status: FileStatus;
  error?: string;
  objectKey?: string;
  uploadId?: string;
};

type NextStatus = "idle" | "working" | "redirecting" | "error";

type AiConsentRecord = {
  granted: boolean;
  version: string;
  timestamp: string;
};

type UploadNextSubmittedProps = {
  onNext?: () => Promise<void> | void;
  conversationId?: string;
};

type CheckoutSessionResponse = {
  url?: string;
  provider?: string;
};

const MAX_FILES = 6;
const AI_CONSENT_VERSION = "2026-03-30";

function inferApiBase(): string {
  try {
    const host = window.location.hostname.toLowerCase();
    const isEu = host.includes(".eu.") || host.startsWith("chat.eu.");
    return isEu
      ? "https://api.eu.heartcheckapp.com/api"
      : "https://api.heartcheckapp.com/api";
  } catch {
    return "https://api.heartcheckapp.com/api";
  }
}

function withCheckoutTracking(
  rawUrl: string,
  userId: string,
  uploadSessionId: string,
  email?: string | null
): string {
  const u = new URL(rawUrl);
  u.searchParams.set("client_reference_id", `${userId}:${uploadSessionId}`);
  if (email) u.searchParams.set("prefilled_email", email);
  u.searchParams.set("hc_ts", String(Date.now()));
  return u.toString();
}

function inferRegionFromWindow(): "eu" | "global" {
  try {
    const host = window.location.hostname.toLowerCase();
    return host.includes(".eu.") || host.startsWith("chat.eu.")
      ? "eu"
      : "global";
  } catch {
    return "global";
  }
}

function expectedAmountForRegion(region: "eu" | "global"): number {
  const key =
    region === "eu"
      ? "NEXT_PUBLIC_PAYMENT_AMOUNT_CENTS_EU"
      : "NEXT_PUBLIC_PAYMENT_AMOUNT_CENTS_GLOBAL";

  const raw =
    typeof process !== "undefined" ? (process.env as any)?.[key] : undefined;
  const n = Number(String(raw ?? "").trim());

  if (Number.isFinite(n) && n > 0) return n;

  return 3999;
}

function expectedCurrencyForRegion(region: "eu" | "global"): string {
  const key =
    region === "eu"
      ? "NEXT_PUBLIC_PAYMENT_CURRENCY_EU"
      : "NEXT_PUBLIC_PAYMENT_CURRENCY_GLOBAL";

  const raw = String(
    (typeof process !== "undefined" ? (process.env as any)?.[key] : "") ?? ""
  )
    .trim()
    .toLowerCase();

  if (raw) return raw;

  return region === "eu" ? "eur" : "usd";
}

function buildAiConsentPayload(consent: AiConsentRecord) {
  return {
    aiConsentGranted: true,
    aiConsentVersion: consent.version,
    aiConsentTimestamp: consent.timestamp,
    aiConsent: {
      granted: true,
      version: consent.version,
      timestamp: consent.timestamp,
    },
  };
}

function UploadNextSubmittedInner({
  onNext,
  conversationId,
}: UploadNextSubmittedProps = {}) {
  const [files, setFiles] = useState<UploadItem[]>([]);
  const [overallStatus, setOverallStatus] = useState<FileStatus>("idle");
  const [nextStatus, setNextStatus] = useState<NextStatus>("idle");
  const [limitNotice, setLimitNotice] = useState<string | null>(null);
  const [showConsent, setShowConsent] = useState(false);

  const { user } = useAuthenticator();

  const [userId, setUserId] = useState<string | null>(null);

  const limitNoticeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const pendingNextRef = useRef<null | (() => Promise<void>)>(null);
  const approvedConsentRef = useRef<AiConsentRecord | null>(null);

  const showTemporaryNotice = useCallback((message: string, ms = 2500) => {
    setLimitNotice(message);

    if (limitNoticeTimeoutRef.current) {
      clearTimeout(limitNoticeTimeoutRef.current);
    }

    limitNoticeTimeoutRef.current = setTimeout(() => {
      setLimitNotice(null);
      limitNoticeTimeoutRef.current = null;
    }, ms);
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const idToken = await getIdTokenWithFallback();
        const sub = extractUserIdFromToken(idToken);
        const fallback =
          (user as any)?.username ??
          (typeof (user as any)?.getUsername === "function"
            ? (user as any).getUsername()
            : undefined) ??
          null;

        if (!cancelled) {
          setUserId(sub ?? fallback ?? null);
        }
      } catch {
        const fallback =
          (user as any)?.username ??
          (typeof (user as any)?.getUsername === "function"
            ? (user as any).getUsername()
            : undefined) ??
          null;

        if (!cancelled) {
          setUserId(fallback ?? null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    return () => {
      if (limitNoticeTimeoutRef.current) {
        clearTimeout(limitNoticeTimeoutRef.current);
        limitNoticeTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!showConsent) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        approvedConsentRef.current = null;
        pendingNextRef.current = null;
        setShowConsent(false);
        setNextStatus("idle");
        showTemporaryNotice(
          "AI consent declined. Wellness summary generation was canceled.",
          3500
        );
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [showConsent, showTemporaryNotice]);

  const uploadSessionId = useMemo(() => uuidv4(), []);

  const apiBase = useMemo(() => inferApiBase(), []);
  const apiEndpointUpload = `${apiBase}/get-upload-url`;
  const apiEndpointNext = `${apiBase}/next`;
  const apiEndpointPay = `${apiBase}/pay`;

  const pickRestJwt = useCallback(async (): Promise<string> => {
    try {
      const session = await Auth.currentSession();
      const accessToken = session.getAccessToken().getJwtToken();
      const idToken = session.getIdToken().getJwtToken();
      const chosen = accessToken || idToken;
      if (chosen) return chosen;
    } catch {
      // fall through
    }

    const idToken = await getIdTokenWithFallback();
    if (idToken) return idToken;

    throw new Error("No Cognito JWT available");
  }, []);

  const getAuthorizationHeaderValue = useCallback(async (): Promise<string> => {
    const jwt = await pickRestJwt();
    return `Bearer ${jwt}`;
  }, [pickRestJwt]);

  const getUserEmail = useCallback(async (): Promise<string | null> => {
    try {
      const ctxEmail = (user as any)?.attributes?.email as string | undefined;
      if (ctxEmail) return ctxEmail;

      const who = await Auth.currentAuthenticatedUser();
      return who?.attributes?.email ?? null;
    } catch {
      return null;
    }
  }, [user]);

  const fileFingerprint = useCallback(
    (f: File) => `${f.name}__${f.size}__${f.lastModified}`,
    []
  );

  const dedupeAndLimit = useCallback(
    (incoming: UploadItem[], existing: UploadItem[]): UploadItem[] => {
      const seen = new Set(existing.map((i) => fileFingerprint(i.file)));
      const merged: UploadItem[] = [...existing];

      for (const item of incoming) {
        const fp = fileFingerprint(item.file);
        if (!seen.has(fp)) {
          merged.push(item);
          seen.add(fp);
        }
        if (merged.length >= MAX_FILES) break;
      }

      return merged.slice(0, MAX_FILES);
    },
    [fileFingerprint]
  );

  const getPresignedUrl = useCallback(
    async (file: File, perFileUploadId: string) => {
      try {
        const authHeader = await getAuthorizationHeaderValue();

        const response = await axios.get(apiEndpointUpload, {
          params: {
            filename: file.name,
            contentType: file.type || "application/octet-stream",
            uploadId: perFileUploadId,
            sessionId: uploadSessionId,
          },
          headers: {
            Authorization: authHeader,
            "Content-Type": "application/json",
          },
          withCredentials: false,
          timeout: 30_000,
        });

        const data = response?.data || {};
        if (!data?.presignedUrl) {
          return null;
        }

        return {
          presignedUrl: String(data.presignedUrl),
          uploadId: String(data.uploadId || perFileUploadId),
          objectKey: String(data.objectKey || ""),
        };
      } catch (error: any) {
        const status = error?.response?.status;
        const body = error?.response?.data;
        console.error(
          "❌ Error fetching presigned URL:",
          status,
          body || error?.message
        );
        return { __errorStatus: status } as any;
      }
    },
    [apiEndpointUpload, getAuthorizationHeaderValue, uploadSessionId]
  );

  const uploadSingleFile = useCallback(
    async (item: UploadItem, perFileUploadId: string): Promise<UploadItem> => {
      const updated: UploadItem = {
        ...item,
        status: "uploading",
        error: undefined,
        uploadId: perFileUploadId,
      };

      try {
        const presign = await getPresignedUrl(item.file, perFileUploadId);

        if (presign && (presign as any).__errorStatus) {
          return {
            ...updated,
            status: "error",
            error: `Failed to fetch upload URL (HTTP ${(presign as any).__errorStatus})`,
          };
        }

        if (!presign) {
          return {
            ...updated,
            status: "error",
            error: "Failed to fetch upload URL",
          };
        }

        await axios.put(presign.presignedUrl, item.file, {
          headers: {
            "Content-Type": item.file.type || "application/octet-stream",
          },
          timeout: 60_000,
        });

        return {
          ...updated,
          status: "done",
          objectKey: presign.objectKey,
          uploadId: presign.uploadId,
        };
      } catch (err: any) {
        console.error("❌ Error uploading file:", err);
        const msg = err?.response?.status
          ? `HTTP ${err.response.status}`
          : "Network Error (likely S3 CORS)";
        return {
          ...updated,
          status: "error",
          error: msg,
        };
      }
    },
    [getPresignedUrl]
  );

  const enqueueFiles = useCallback(
    (selected: File[]) => {
      if (!selected || selected.length === 0) return;

      const incoming: UploadItem[] = selected.map((f): UploadItem => ({
        id: uuidv4(),
        file: f,
        status: "idle",
      }));

      setFiles((prev) => {
        const merged = dedupeAndLimit(incoming, prev);

        if (merged.length === prev.length && incoming.length > 0) {
          showTemporaryNotice(`You can queue up to ${MAX_FILES} files.`);
        }

        if (merged.length > 0) {
          setOverallStatus("idle");
        }

        return merged;
      });
    },
    [dedupeAndLimit, showTemporaryNotice]
  );

  const openFilePicker = useCallback(async () => {
    if (files.length >= MAX_FILES) {
      showTemporaryNotice(`You can queue up to ${MAX_FILES} files.`);
      return;
    }

    const anyWindow: any = window as any;
    const isUserGestureActive =
      ((navigator as any)?.userActivation?.isActive) ?? true;

    if (
      typeof anyWindow.showOpenFilePicker === "function" &&
      isUserGestureActive
    ) {
      try {
        const handles: any[] = await anyWindow.showOpenFilePicker({
          multiple: true,
          types: [
            {
              description: "Documents & Images",
              accept: {
                "application/pdf": [".pdf"],
                "image/*": [
                  ".jpg",
                  ".jpeg",
                  ".png",
                  ".gif",
                  ".webp",
                  ".tif",
                  ".tiff",
                ],
                "text/plain": [".txt"],
              },
            },
          ],
        });

        const picked: File[] = [];
        for (const h of handles) {
          try {
            const file = await h.getFile();
            picked.push(file);
          } catch {
            // ignore single failed handle
          }
        }

        if (picked.length > 0) {
          enqueueFiles(picked);
          return;
        }
      } catch {
        // fall through
      }
    }

    const temp = document.createElement("input");
    temp.type = "file";
    temp.multiple = true;
    temp.accept = ".pdf,.PDF,.jpg,.jpeg,.png,.gif,.webp,.tif,.tiff,.txt";
    temp.style.position = "fixed";
    temp.style.left = "-9999px";
    temp.style.width = "0";
    temp.style.height = "0";
    temp.style.opacity = "0";
    temp.setAttribute("aria-hidden", "true");
    temp.tabIndex = -1;

    const cleanup = () => {
      temp.removeEventListener("change", handleChange);
      if (temp.parentNode) temp.parentNode.removeChild(temp);
    };

    const handleChange = () => {
      const list = temp.files ? Array.from(temp.files) : [];
      if (list.length > 0) enqueueFiles(list);
      cleanup();
    };

    temp.addEventListener("change", handleChange, { once: true });
    document.body.appendChild(temp);

    Promise.resolve().then(() => {
      try {
        const anyInput: any = temp as any;
        if (typeof anyInput.showPicker === "function") {
          anyInput.showPicker();
        } else {
          temp.click();
        }
      } catch {
        try {
          temp.click();
        } catch {
          // ignore
        }
      }
    });
  }, [enqueueFiles, files.length, showTemporaryNotice]);

  const handleUpload = useCallback(async () => {
    if (!files.length) {
      await openFilePicker();
      return;
    }

    setOverallStatus("uploading");

    const withIds: UploadItem[] = files.map((f): UploadItem => ({
      ...f,
      status: "uploading",
      error: undefined,
      uploadId: f.uploadId ?? uuidv4(),
    }));

    setFiles(withIds);

    const promises = withIds.map((f) => uploadSingleFile(f, f.uploadId!));
    const results = await Promise.allSettled(promises);

    const merged: UploadItem[] = withIds.map((f, idx): UploadItem => {
      const r = results[idx];
      if (r.status === "fulfilled") return r.value;
      return {
        ...f,
        status: "error",
        error: (r as PromiseRejectedResult).reason?.message || "Upload failed",
      };
    });

    setFiles(merged);

    const anyError = merged.some((r) => r.status === "error");
    const allDone = merged.every((r) => r.status === "done");
    setOverallStatus(anyError ? "error" : allDone ? "done" : "idle");

    if (userId && merged.some((r) => r.status === "done")) {
      try {
        const mutation = new GraphqlQuery<{ createUserEvent: { id: string } }>(`
          mutation StoreUserEvent($input: CreateUserEventInput!) {
            createUserEvent(input: $input) { id }
          }
        `);

        await mutation.invoke({
          input: {
            userId,
            timestamp: new Date().toISOString(),
            eventType: "UPLOAD_COMPLETED",
          },
        });
      } catch (e) {
        console.warn("⚠️ Failed to log UPLOAD_COMPLETED event", e);
      }
    }
  }, [files, openFilePicker, uploadSingleFile, userId]);

  const invokeAPI = useCallback(async () => {
    try {
      if (!userId) {
        console.error("❌ Cannot invoke workflow — userId not resolved.");
        setNextStatus("error");
        return;
      }

      const consent = approvedConsentRef.current;
      if (
        !consent ||
        !consent.granted ||
        consent.version !== AI_CONSENT_VERSION
      ) {
        console.error(
          "❌ Cannot invoke workflow — AI consent missing for this Next press."
        );
        pendingNextRef.current = async () => {
          await proceedWithNextRef.current();
        };
        setShowConsent(true);
        setNextStatus("idle");
        return;
      }

      setNextStatus("working");

      const consentPayload = buildAiConsentPayload(consent);
      const authHeader = await getAuthorizationHeaderValue();
      const email = await getUserEmail();
      const region = inferRegionFromWindow();
      const expectedAmountCents = expectedAmountForRegion(region);
      const expectedCurrency = expectedCurrencyForRegion(region);

      const nextPayload = {
        userId,
        conversationId,
        uploadId: uploadSessionId,
        uploadSessionId,
        timestamp: new Date().toISOString(),
        email,
        region,
        expectedAmountCents,
        expectedCurrency,
        ...consentPayload,
      };

      console.log("[UploadNextSubmitted] 📦 EXACT /next payload", nextPayload);
      console.log(
        "[UploadNextSubmitted] 📦 EXACT /next payload JSON",
        JSON.stringify(nextPayload)
      );

      await axios.post(apiEndpointNext, nextPayload, {
        headers: {
          Authorization: authHeader,
          "Content-Type": "application/json",
        },
        withCredentials: false,
        timeout: 30_000,
      });

      const payPayload = {
        uploadId: uploadSessionId,
        uploadSessionId,
        userId,
        conversationId,
        region,
        expectedAmountCents,
        expectedCurrency,
        ...consentPayload,
      };

      console.log("[UploadNextSubmitted] 💳 EXACT /pay payload", payPayload);
      console.log(
        "[UploadNextSubmitted] 💳 EXACT /pay payload JSON",
        JSON.stringify(payPayload)
      );

      const payResp = await axios.post<CheckoutSessionResponse>(
        apiEndpointPay,
        payPayload,
        {
          headers: {
            Authorization: authHeader,
            "Content-Type": "application/json",
          },
          withCredentials: false,
          timeout: 30_000,
        }
      );

      const checkoutUrl = payResp?.data?.url;
      if (checkoutUrl) {
        const redirectUrl = withCheckoutTracking(
          checkoutUrl,
          userId,
          uploadSessionId,
          email
        );
        setNextStatus("redirecting");
        setTimeout(() => window.location.assign(redirectUrl), 600);
      } else {
        console.error(
          "❌ /api/pay did not return a checkout URL:",
          payResp?.data
        );
        setNextStatus("error");
      }
    } catch (error: any) {
      console.error(
        "❌ Error invoking workflow or starting checkout:",
        error?.response?.status,
        error?.response?.data || error?.message
      );
      setNextStatus("error");
    } finally {
      approvedConsentRef.current = null;
      pendingNextRef.current = null;
    }
  }, [
    apiEndpointNext,
    apiEndpointPay,
    conversationId,
    getAuthorizationHeaderValue,
    getUserEmail,
    uploadSessionId,
    userId,
  ]);

  const proceedWithNext = useCallback(async () => {
    if (onNext) {
      try {
        setNextStatus("working");
        await onNext();
        setNextStatus("idle");
      } catch (error: any) {
        console.error("❌ Error in custom onNext:", error?.message || error);
        setNextStatus("error");
      } finally {
        approvedConsentRef.current = null;
        pendingNextRef.current = null;
      }
      return;
    }

    await invokeAPI();
  }, [invokeAPI, onNext]);

  const proceedWithNextRef = useRef(proceedWithNext);

  useEffect(() => {
    proceedWithNextRef.current = proceedWithNext;
  }, [proceedWithNext]);

  const handleConsentAgree = useCallback(async () => {
    try {
      approvedConsentRef.current = {
        granted: true,
        version: AI_CONSENT_VERSION,
        timestamp: new Date().toISOString(),
      };

      setShowConsent(false);

      const pendingNext = pendingNextRef.current;
      pendingNextRef.current = null;

      if (pendingNext) {
        setTimeout(() => {
          void pendingNext();
        }, 100);
      }
    } catch (error) {
      console.error("❌ Failed to capture AI consent:", error);
      setNextStatus("error");
    }
  }, []);

  const handleConsentDecline = useCallback(() => {
    approvedConsentRef.current = null;
    pendingNextRef.current = null;
    setShowConsent(false);
    setNextStatus("idle");
    showTemporaryNotice(
      "AI consent declined. Wellness summary generation was canceled.",
      3500
    );
  }, [showTemporaryNotice]);

  const handleNextClick = useCallback(async () => {
    approvedConsentRef.current = null;
    pendingNextRef.current = async () => {
      await proceedWithNext();
    };
    setShowConsent(true);
  }, [proceedWithNext]);

  const uploadingOrRedirecting =
    overallStatus === "uploading" ||
    nextStatus === "working" ||
    nextStatus === "redirecting";

  return (
    <View position="relative">
      <Container heading="Actions">
        {limitNotice && (
          <Text marginTop="0.5rem" color="rebeccapurple">
            {limitNotice}
          </Text>
        )}

        {files.length > 0 && (
          <View marginTop="0.5rem" marginBottom="0.5rem">
            {files.map((f) => (
              <Flex
                key={f.id}
                alignItems="center"
                gap={8}
                marginTop="0.25rem"
                wrap="wrap"
              >
                <Text>{f.file.name}</Text>
                {f.status === "idle" && <Badge variation="info">ready</Badge>}
                {f.status === "uploading" && (
                  <Badge variation="warning">uploading…</Badge>
                )}
                {f.status === "done" && (
                  <Badge variation="success">uploaded</Badge>
                )}
                {f.status === "error" && <Badge variation="error">error</Badge>}
                {f.error && <Text color="red">{f.error}</Text>}
                <Button
                  size="small"
                  variation="link"
                  onClick={() => {
                    setFiles((prev) => {
                      const next = prev.filter((x) => x.id !== f.id);
                      if (next.length === 0) setOverallStatus("idle");
                      return next;
                    });
                  }}
                  isDisabled={f.status === "uploading"}
                >
                  Remove
                </Button>
              </Flex>
            ))}

            <Text fontSize="0.85rem" color="font.secondary" marginTop="0.5rem">
              {files.length}/{MAX_FILES} queued
            </Text>
          </View>
        )}

        <Flex direction="column" gap={4} alignItems="start">
          <Flex gap={8} alignItems="center">
            <Button
              variation="primary"
              size="small"
              type="button"
              className="w-[150px] mt-4"
              onClick={() => void openFilePicker()}
              onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) => {
                if (e.key === "Enter" || e.key === " ") void openFilePicker();
              }}
              isDisabled={uploadingOrRedirecting || showConsent}
              data-testid="select-files-button"
            >
              Select Files
            </Button>

            <Button
              variation="primary"
              size="small"
              type="button"
              className="w-[150px] mt-4"
              onClick={() => void handleUpload()}
              isDisabled={
                uploadingOrRedirecting || files.length === 0 || showConsent
              }
            >
              Upload
            </Button>
          </Flex>

          {overallStatus === "done" && (
            <Text>✅ All selected files uploaded successfully</Text>
          )}
          {overallStatus === "error" && (
            <Text color="red">❌ Some files failed to upload</Text>
          )}
        </Flex>

        <Flex direction="column" gap={4} alignItems="start">
          <br />
          <Button
            isFullWidth
            variation="primary"
            size="small"
            type="button"
            className="w-[150px] mt-4"
            onClick={() => void handleNextClick()}
            isDisabled={
              nextStatus === "working" ||
              nextStatus === "redirecting" ||
              showConsent
            }
          >
            {nextStatus === "working" ? "Working…" : "Next"}
          </Button>

          {nextStatus === "redirecting" && (
            <span role="status" aria-live="polite">
              👉 We’re redirecting you to the secure checkout page…
            </span>
          )}

          {nextStatus === "error" && (
            <span role="alert">
              ❌ We couldn’t start checkout. Please try again.
            </span>
          )}
        </Flex>
      </Container>

      {showConsent && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="ai-consent-title"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "1rem",
            zIndex: 9999,
          }}
          onClick={handleConsentDecline}
        >
          <div
            style={{
              background: "#fff",
              color: "#111827",
              width: "100%",
              maxWidth: "760px",
              borderRadius: "16px",
              boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
              overflow: "hidden",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: "1.25rem 1.25rem 0.75rem 1.25rem" }}>
              <h2
                id="ai-consent-title"
                style={{ margin: 0, fontSize: "1.4rem", fontWeight: 700 }}
              >
                Consent to AI Processing
              </h2>
            </div>

            <div
              style={{
                padding: "0 1.25rem 1rem 1.25rem",
                maxHeight: "55vh",
                overflowY: "auto",
                lineHeight: 1.55,
              }}
            >
              <p style={{ marginTop: 0 }}>
                To generate your wellness summary, heartcheckapp may send
                information you provide — such as chat responses, uploaded
                documents, and extracted health-related text — to Amazon Web
                Services, including Amazon Bedrock Service&apos;s Anthropic
                Claude Sonnet Model, for AI processing. Direct personal
                identifiers are removed before AI processing when possible, but
                the information may still relate to your health.
              </p>

              <p>
                We use this information only to generate your educational
                wellness summary and improve delivery of that service. It is not
                used for advertising.
              </p>

              <p>Please review and choose whether you want to continue.</p>

              <ul style={{ paddingLeft: "1.25rem", marginBottom: 0 }}>
                <li>
                  Data that may be sent: chat responses, uploaded document text,
                  extracted medical or wellness information, language preference,
                  and technical request metadata
                </li>
                <li>
                  Sent to: Amazon Web Services, including Amazon Bedrock
                </li>
                <li>Purpose: generate your requested wellness summary</li>
                <li>Not used for: advertising or sale of personal data</li>
                <li>
                  You can decline, but AI summary generation will not be
                  available
                </li>
              </ul>
            </div>

            <div
              style={{
                display: "flex",
                gap: "0.75rem",
                justifyContent: "flex-end",
                padding: "1rem 1.25rem 1.25rem 1.25rem",
                borderTop: "1px solid #e5e7eb",
              }}
            >
              <Button variation="link" onClick={handleConsentDecline}>
                I Decline
              </Button>

              <Button
                variation="primary"
                onClick={() => void handleConsentAgree()}
              >
                I Agree
              </Button>
            </div>
          </div>
        </div>
      )}
    </View>
  );
}

export default function UploadNextSubmitted(props: UploadNextSubmittedProps) {
  return <UploadNextSubmittedInner {...props} />;
}

export { UploadNextSubmittedInner as UploadNextSubmittedInnerComponent };
export const UploadNextRendered = UploadNextSubmittedInner;
